import {
  Coordinates,
  LeadCategory,
  OpeningTimes,
  RoomClosedReason,
  UsageSpaceCategory,
  UsageSpaceInfo,
} from './lead.model';
import { components } from '../types/backend';

export interface ExaminationResult {
  id: string;
  startTimeIso8601: string;
  startCoordinates: Coordinates;
  startReason?: TooFarReason;
  name?: string;
  operatorName?: string;
  leadCategory?: LeadCategory;
  tel?: string;
  website?: string;
  openingTimes?: OpeningTimes;
  notes?: string;
  notPossibleReason?: NotPossibleReason;
  images: ImageWithDescription[];
  roomExaminations: RoomExaminationResult[];
  selfCreatedRooms: SelfCreatedRoom[];
  currentRoomExaminationId?: string;
  playbackDevicesVisible: boolean;
  playbackDevicesLocation: PlaybackDeviceLocation | null;
  guestsPresent: boolean;
  guestCount: number | null;
  // Stable ID for one submission attempt; reused on retries for backend idempotency (SR-03)
  submissionId?: string;
}

export interface ImageWithDescription {
  image: string;
  description?: string;
}

export interface RoomExaminationResult {
  id: string;
  info: RoomExaminationResultInfo;
}

export type TvSize = 'below-65-inches' | 'above-65-inches';
export const tvSizes: TvSize[] = ['below-65-inches', 'above-65-inches'];
export type VideoSize = 'below-42-inches' | 'above-42-inches';
export const videoSizes: VideoSize[] = ['below-42-inches', 'above-42-inches'];

export type PlaybackDeviceLocation = 'NOT_VISIBLE' | 'HINTER_UEBER_THEKE' | 'WAND' | 'REGAL' | 'DECKE' | 'FASSADE_MAST';
export const playbackDeviceLocations: PlaybackDeviceLocation[] = ['NOT_VISIBLE', 'HINTER_UEBER_THEKE', 'WAND', 'REGAL', 'DECKE', 'FASSADE_MAST'];

export type RadioType = 'public' | 'private' | 'not-determinable';
export type TvBroadcastType = 'public' | 'private' | 'not-determinable';

export interface Tariffs {
  audio: boolean;
  radio: boolean;
  radioType: RadioType | null;    // FR-14: replaces radioPublic + radioPrivate
  radioStation: string | null;    // FR-14: new
  audioOrRadio: boolean;
  video: boolean;
  videoSize: VideoSize;
  tv: boolean;
  tvType: TvBroadcastType | null; // FR-14: replaces tvPublic + tvPrivate
  tvStation: string | null;       // FR-14: new
  tvSize: TvSize;
  tvQuantity: number | null;
}

export interface RoomExaminationResultInfo {
  tariffs: Tariffs;
  images: ImageWithDescription[];
  audioUrls: string[];
  roomNotes?: string;
  name?: string;
  size?: number;
  usageSpaceCategory?: UsageSpaceCategory;
  roomIsClosed: boolean;
  roomIsClosedReason?: RoomClosedReason;
}

export interface SelfCreatedRoom {
  selfAssignedId: string;
  info: UsageSpaceInfo;
}

export function getDefaultTariffs(): Tariffs {
  return {
    audio: false,
    radio: false,
    radioType: null,
    radioStation: null,
    audioOrRadio: false,
    video: false,
    videoSize: 'above-42-inches',
    tv: false,
    tvType: null,
    tvStation: null,
    tvSize: 'below-65-inches',
    tvQuantity: 1,
  };
}

export type TooFarReason = 'NO_SIGNAL' | 'OTHER';
export const TOO_FAR_REASONS: TooFarReason[] = ['NO_SIGNAL', 'OTHER'] as const;

export type NotPossibleReason =
  components['schemas']['ExaminationResultDTO']['notPossibleReason'];
export const NOT_POSSIBLE_REASONS: NotPossibleReason[] = [
  'CLIENT_CONTACT',
  'BANNED_FROM_PREMISES',
  'VACANCY',
  'NO_LONGER_EXISTS',
  'CURRENTLY_CLOSED',
  'LOCATION_INACCESSIBLE',
  'COVERAGE_COSTS',
] as const;
